/**
 * app/components/ThemedButton.tsx
 * --------------------------------
 * What: Button component styled with the active theme and common states.
 * Why: Reduces duplication and keeps buttons visually consistent.
 */
import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { useTheme } from '../../lib/ThemeContext';

interface Props {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  style?: ViewStyle;
  textStyle?: TextStyle;
  disabled?: boolean;
  accessibilityLabel?: string;
}

const ThemedButton: React.FC<Props> = ({
  title,
  onPress,
  variant = 'primary',
  style,
  textStyle,
  disabled,
  accessibilityLabel,
}) => {
  const { theme } = useTheme();

  const backgroundColor =
    variant === 'primary' ? theme.accent :
    variant === 'secondary' ? theme.inputBackground :
    theme.error;

  const borderColor =
    variant === 'primary' ? theme.accent :
    variant === 'secondary' ? theme.border :
    theme.error;

  const textColor =
    variant === 'primary' ? theme.buttonText :
    variant === 'secondary' ? theme.text :
    '#fff';

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[
        styles.button,
        {
          backgroundColor: disabled ? theme.border : backgroundColor,
          borderColor,
        },
        style,
      ]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || title}
      activeOpacity={0.85}
    >
      <Text style={[styles.label, { color: textColor }, textStyle]}>{title}</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    textAlign: 'center',
  },
});

export default ThemedButton; 